import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, Search, Filter, Brain, MessageSquare, Lightbulb } from 'lucide-react';

export default function AITraining() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('faqs'); // 'faqs' or 'directives'
  const [faqs, setFaqs] = useState([]);
  const [directives, setDirectives] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [formData, setFormData] = useState({
    store_ids: [],
    question: '',
    answer: '',
    content: ''
  });

  useEffect(() => {
    Promise.all([fetchFaqs(), fetchDirectives(), fetchStores()]).then(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'faqs') {
      fetchFaqs();
    } else {
      fetchDirectives();
    }
  }, [searchQuery, storeFilter, activeTab]);

  const fetchFaqs = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (storeFilter) params.append('store_id', storeFilter);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/settings/faqs${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('S.S.S yüklenirken hata oluştu');
    }
  };

  const fetchDirectives = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (storeFilter) params.append('store_id', storeFilter);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/settings/directives${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDirectives(data);
      }
    } catch (error) {
      console.error('Error fetching Directives:', error);
      toast.error('Direktifler yüklenirken hata oluştu');
    }
  };

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stores`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Mağazalar yüklenirken hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.store_ids.length === 0) {
      toast.error('En az bir mağaza seçmelisiniz');
      return;
    }
    
    try {
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'directives';
      const url = editingItem
        ? `${process.env.REACT_APP_BACKEND_URL}/api/settings/${endpoint}/${editingItem.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/settings/${endpoint}`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const body = activeTab === 'faqs' 
        ? { store_ids: formData.store_ids, question: formData.question, answer: formData.answer }
        : { store_ids: formData.store_ids, content: formData.content };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Güncellendi' : 'Eklendi');
        setShowDialog(false);
        setEditingItem(null);
        resetForm();
        if (activeTab === 'faqs') {
          fetchFaqs();
        } else {
          fetchDirectives();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    if (activeTab === 'faqs') {
      setFormData({
        store_ids: item.store_ids || [],
        question: item.question,
        answer: item.answer,
        content: ''
      });
    } else {
      setFormData({
        store_ids: item.store_ids || [],
        question: '',
        answer: '',
        content: item.content
      });
    }
    setShowDialog(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      const endpoint = activeTab === 'faqs' ? 'faqs' : 'directives';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/${endpoint}/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Silindi');
        if (activeTab === 'faqs') {
          fetchFaqs();
        } else {
          fetchDirectives();
        }
      } else {
        toast.error('Silme işlemi başarısız');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const toggleStoreSelection = (storeId) => {
    setFormData(prev => {
      const newStoreIds = prev.store_ids.includes(storeId)
        ? prev.store_ids.filter(id => id !== storeId)
        : [...prev.store_ids, storeId];
      
      return { ...prev, store_ids: newStoreIds };
    });
  };

  const selectAllStores = () => {
    setFormData(prev => ({
      ...prev,
      store_ids: stores.map(s => s.id)
    }));
  };

  const deselectAllStores = () => {
    setFormData(prev => ({
      ...prev,
      store_ids: []
    }));
  };

  const resetForm = () => {
    setFormData({
      store_ids: [],
      question: '',
      answer: '',
      content: ''
    });
  };

  const getStoreNames = (storeIds) => {
    if (!storeIds || storeIds.length === 0) return 'Mağaza seçilmemiş';
    
    const names = storeIds
      .map(id => {
        const store = stores.find(s => s.id === id);
        return store ? store.store_name : null;
      })
      .filter(Boolean);
    
    return names.length > 0 ? names.join(', ') : 'Mağaza bulunamadı';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-orange-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Yapay Zekayı Eğit</h1>
                <p className="mt-1 text-sm text-white/80">
                  AI'nızı sık sorulan sorular ve direktiflerle eğitin
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingItem(null);
                resetForm();
                setShowDialog(true);
              }}
              className="inline-flex items-center px-6 py-3 text-base font-semibold rounded-lg shadow-lg text-white bg-white/30 backdrop-blur-sm hover:bg-white/40 hover:shadow-xl transition-all transform hover:scale-105 border-2 border-white"
            >
              <Plus className="w-6 h-6 mr-2" />
              Yeni Ekle
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('faqs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'faqs'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-5 h-5" />
                <span>Sık Sorulan Sorular</span>
                <span className="ml-2 py-0.5 px-2 rounded-full bg-orange-100 text-orange-600 text-xs font-medium">
                  {faqs.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('directives')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'directives'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Lightbulb className="w-5 h-5" />
                <span>AI Direktifleri</span>
                <span className="ml-2 py-0.5 px-2 rounded-full bg-purple-100 text-purple-600 text-xs font-medium">
                  {directives.length}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'faqs' ? 'S.S.S içinde ara...' : 'Direktiflerde ara...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="sm:w-64">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
              >
                <option value="">Tüm Mağazalar</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.store_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Items List */}
        {activeTab === 'faqs' ? (
          // FAQs List
          <div className="space-y-4">
            {faqs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Henüz S.S.S eklenmemiş</p>
                <p className="text-sm text-gray-400 mt-2">
                  AI'nızı sık sorulan sorularla eğitin
                </p>
              </div>
            ) : (
              faqs.map((faq) => (
                <div key={faq.id} className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <MessageSquare className="w-5 h-5 text-orange-500" />
                        <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                      </div>
                      <p className="text-gray-600 mb-3">{faq.answer}</p>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span className="font-medium">Mağazalar:</span>
                        <span>{getStoreNames(faq.store_ids)}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(faq)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(faq.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          // Directives List
          <div className="space-y-4">
            {directives.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Henüz direktif eklenmemiş</p>
                <p className="text-sm text-gray-400 mt-2">
                  AI'nızın nasıl davranacağını direktiflerle belirleyin
                </p>
              </div>
            ) : (
              directives.map((directive) => (
                <div key={directive.id} className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow border-l-4 border-purple-500">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-3">
                        <Lightbulb className="w-5 h-5 text-purple-500" />
                      </div>
                      <p className="text-gray-700 text-base mb-3 whitespace-pre-wrap leading-relaxed">{directive.content}</p>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span className="font-medium">Mağazalar:</span>
                        <span>{getStoreNames(directive.store_ids)}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEdit(directive)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(directive.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingItem ? 'Düzenle' : 'Yeni Ekle'}
                </h2>
                <button
                  onClick={() => {
                    setShowDialog(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Store Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mağazalar *
                  </label>
                  <div className="mb-2 flex space-x-2">
                    <button
                      type="button"
                      onClick={selectAllStores}
                      className="text-xs text-orange-600 hover:text-orange-700"
                    >
                      Tümünü Seç
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={deselectAllStores}
                      className="text-xs text-orange-600 hover:text-orange-700"
                    >
                      Tümünü Kaldır
                    </button>
                  </div>
                  <div className="border border-gray-300 rounded-md p-4 max-h-40 overflow-y-auto">
                    {stores.length === 0 ? (
                      <p className="text-sm text-gray-500">Mağaza bulunamadı</p>
                    ) : (
                      <div className="space-y-2">
                        {stores.map((store) => (
                          <label key={store.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.store_ids.includes(store.id)}
                              onChange={() => toggleStoreSelection(store.id)}
                              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{store.store_name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {activeTab === 'faqs' ? (
                  // FAQ Form
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Soru *
                      </label>
                      <input
                        type="text"
                        value={formData.question}
                        onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Örn: Kargo ücreti ne kadar?"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cevap *
                      </label>
                      <textarea
                        value={formData.answer}
                        onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Örn: 50 TL üzeri siparişlerde kargo ücretsizdir."
                        required
                      />
                    </div>
                  </>
                ) : (
                  // Directive Form
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Direktif *
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Örn: Müşterilerimize her zaman samimi ve yardımsever bir dille yaklaşın. Premium bir hizmet sunduğumuzu hissettirin. Ürünlerimizin kalitesini ve özelliklerini vurgulayın."
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Bu direktif AI'nın cevap verirken nasıl davranacağını belirler
                      </p>
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDialog(false);
                      setEditingItem(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                      activeTab === 'faqs' 
                        ? 'bg-orange-600 hover:bg-orange-700' 
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {editingItem ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
